import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Chip,
  Rating,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider as MuiSlider,
  CircularProgress
} from '@mui/material';
import { AddShoppingCart as AddShoppingCartIcon } from '@mui/icons-material';
import { useCart } from '../../context/CartContext';
import { Product, Category } from '../../services/FirestoreService';
import { getProducts, getProductsByCategory, getCategories } from '../../services/FirestoreService';
import { useParams } from 'react-router-dom';

const CategoryPage: React.FC = () => {
  const { state, dispatch } = useCart();
  const { id } = useParams<{ id: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sortOption, setSortOption] = useState('default');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 300]); // Will be updated to actual max price
  const [maxPrice, setMaxPrice] = useState<number>(300); // Track actual max price
  const [stockFilter, setStockFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch categories
        const fetchedCategories = await getCategories();
        setCategories(fetchedCategories);
        
        // Determine which category to load products for
        // Prioritize selectedCategory from dropdown, fallback to URL id, then to all products
        const categoryToLoad = selectedCategory !== 'all' && selectedCategory !== 'default' ? selectedCategory : id || null;
        
        let fetchedProducts: Product[] = [];
        
        if (categoryToLoad && categoryToLoad !== 'all') {
          // Fetch products for the specific category
          setSelectedCategory(categoryToLoad);
          fetchedProducts = await getProductsByCategory(categoryToLoad);
        } else {
          // Fetch all products
          setSelectedCategory('all');
          fetchedProducts = await getProducts();
        }
        
        setProducts(fetchedProducts);

        // Calculate max price from the fetched products
        if (fetchedProducts.length > 0) {
          const maxProductPrice = Math.max(
            ...fetchedProducts.map(p => p.discountedPrice ?? p.price)
          );
          setMaxPrice(maxProductPrice);
          // Set the price range with the actual max price
          setPriceRange([0, maxProductPrice]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, selectedCategory]);

  // Apply filters and sorting (excluding category since we now fetch products by category directly)
  useEffect(() => {
    let result = [...products];
    
    // Apply stock filter
    if (stockFilter === 'in-stock') {
      result = result.filter(product => product.stock > 0);
    }
    
    // Apply price range filter
    result = result.filter(product => 
      (product.discountedPrice ?? product.price) >= priceRange[0] && 
      (product.discountedPrice ?? product.price) <= priceRange[1]
    );
    
    // Apply sorting
    switch (sortOption) {
      case 'price-asc':
        result.sort((a, b) => (a.discountedPrice ?? a.price) - (b.discountedPrice ?? b.price));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.discountedPrice ?? b.price) - (a.discountedPrice ?? a.price));
        break;
      case 'top-rated':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      default:
        // Default sorting
        break;
    }
    
    setFilteredProducts(result);
  }, [products, sortOption, priceRange, stockFilter]);

  const handleAddToCart = (product: Product) => {
    // Check if adding 1 item exceeds stock
    const existingItem = state.items.find(item => item.id === product.id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    const newQuantity = currentQuantity + 1;
    
    if (newQuantity > product.stock) {
      alert(`Cannot add item. Only ${product.stock} items available in stock.`);
      return;
    }
    
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        name: product.name,
        price: product.price,
        discountedPrice: product.discountedPrice,
        quantity: 1,
        image: product.image
      }
    });
  };

  const handlePriceRangeChange = (event: Event, newValue: number | number[]) => {
    setPriceRange(newValue as [number, number]);
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <CircularProgress />
      </Container>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <Typography variant="h4" component="h1" className="mb-6 text-2xl md:text-3xl">
        Ürün Kategorileri
      </Typography>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters Sidebar - Hidden on mobile by default, can be shown with a button */}
        <div className="md:w-1/4">
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4">
                Filtreler
              </Typography>
              
              <div className="mb-4">
                <FormControl fullWidth>
                  <InputLabel id="category-select-label">Kategori</InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={selectedCategory}
                    label="Kategori"
                    onChange={(e) => setSelectedCategory(e.target.value as string)}
                  >
                    <MenuItem value="all">Tüm Kategoriler</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              
              <div className="mb-4">
                <Typography variant="subtitle1" className="mb-2">
                  Fiyat Aralığı
                </Typography>
                <MuiSlider
                  value={priceRange}
                  onChange={handlePriceRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={maxPrice}
                  className="mb-2"
                />
                <div className="flex justify-between">
                  <Typography variant="body2">₺{priceRange[0]}</Typography>
                  <Typography variant="body2">₺{priceRange[1]}</Typography>
                </div>
              </div>
              
              <div className="mb-4">
                <Typography variant="subtitle1" className="mb-2">
                  Sıralama
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value as string)}
                  >
                    <MenuItem value="default">Varsayılan</MenuItem>
                    <MenuItem value="price-asc">Fiyat: Düşükten Yükseğe</MenuItem>
                    <MenuItem value="price-desc">Fiyat: Yüksekten Düşüğe</MenuItem>
                    <MenuItem value="top-rated">En Çok Oylanan</MenuItem>
                    <MenuItem value="newest">Yeniden Eskiye</MenuItem>
                  </Select>
                </FormControl>
              </div>
              
              <div>
                <Typography variant="subtitle1" className="mb-2">
                  Stok Durumu
                </Typography>
                <Button
                  variant={stockFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setStockFilter('all')}
                  size="small"
                  className="mr-2 mb-2"
                >
                  Tümü
                </Button>
                <Button
                  variant={stockFilter === 'in-stock' ? 'contained' : 'outlined'}
                  onClick={() => setStockFilter('in-stock')}
                  size="small"
                  className="mb-2"
                >
                  Stokta Olanlar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Products Grid */}
        <div className="md:w-3/4">
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="text-lg">
              {filteredProducts.length} ürün
            </Typography>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map((product) => (
              <Card 
                key={product.id}
                className="h-full flex flex-col relative"
              >
                <div className="relative">
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.image}
                    alt={product.name}
                    onClick={() => window.location.href = `/product/${product.id}`}
                    sx={{ cursor: 'pointer' }}
                  />
                  {product.discount && product.discount > 0 && (
                    <Chip
                      label={`${product.discount}%`}
                      color="error"
                      size="small"
                      className="absolute top-2 right-2 rounded-sm"
                    />
                  )}
                  {product.isNew && (
                    <Chip
                      label="Yeni"
                      color="success"
                      size="small"
                      className="absolute top-2 left-2 rounded-sm"
                    />
                  )}
                  {product.stock === 0 && (
                    <div
                      className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 
                                flex items-center justify-center text-white text-xl font-bold"
                    >
                      STOKTA YOK
                    </div>
                  )}
                </div>
                <CardContent className="flex-grow">
                  <Typography variant="body2" component="div" className="text-sm">
                    {product.name}
                  </Typography>
                  <Rating value={product.rating || 0} readOnly size="small" className="mt-1" />
                  <div className="mt-2 flex items-center">
                    {product.discountedPrice ? (
                      <>
                        <Typography variant="body1" color="error">
                          ₺{product.discountedPrice}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" className="ml-2 line-through">
                          ₺{product.price}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" color="error">
                        ₺{product.price}
                      </Typography>
                    )}
                  </div>
                  <Typography variant="body2" color="textSecondary" className="mt-1">
                    Stok: {product.stock > 0 ? `${product.stock} adet` : 'Stokta yok'}
                  </Typography>
                </CardContent>
                <div className="p-2 flex justify-between">
                  <Button 
                    variant="outlined" 
                    size="small"
                    component="a"
                    href={`/product/${product.id}`}
                  >
                    Detaya Git
                  </Button>
                  <Button 
                    variant="contained" 
                    size="small"
                    startIcon={<AddShoppingCartIcon />}
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock === 0}
                  >
                    Sepete Ekle
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-8">
              <Typography variant="h6">
                Seçilen filtrelerle eşleşen ürün bulunamadı.
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;